import React, { useContext, useState, useEffect, useRef } from 'react';
import {
	Box,
	Button,
	Flex,
	HStack,
	Skeleton,
	Text,
	useDisclosure,
	Wrap,
	WrapItem,
} from 'src/components/ui';
import {
	IChannelCreativeAttributes,
	IChannelGroup,
	ICampaign,
} from 'src/lib/schemas';
import DDCard from './DDCard';
import GenerateOrSelectCard from './GenerateOrSelectCard';
import TemplateContext from 'src/contexts/templates/TemplatesContext';
import useToggleWithPayload from 'src/hooks/useToggleWithPayload';
import { removeDuplicateLayers } from '../utils/removeDuplicateLayers';
import { AssistantChatContext, CampaignContext } from 'src/contexts';
import {
	getCampaign,
	removeDesignDirection,
	genrateCampaignCreatives,
	regenerateCampaignDesignDirection,
	genrateNewDesignDirection,
	editCampaignDesignDirections,
} from 'src/services/campaign';
import { toastError, toastSuccess } from 'src/services/toast';
import Creatives from '../creatives/Creatives';
import EmptyState from './EmptyState';
import { getTemplateLayers } from 'src/services/templates';
import { processCallback } from 'src/lib/utils/processCallback';
import {
	getHighestScoreTemplate,
	getTemplateIdsFromDesignDirections,
} from 'src/lib/utils/templates/templates';
import {
	ICreative,
	IDesignDirection,
} from 'src/lib/schemas/campaign/newFlowCampaign';
import Design from 'src/pages/design/Design';
import ConfirmDialog from 'src/components/common/ConfirmDialog';
import { getFonts } from 'src/services/config';
import { RIGHT_SIDEBAR_WIDTH_COLLAPSED } from '../utils/constants';

interface DesignDirectionsProps {
	creativesConfig: IChannelCreativeAttributes[];
	onSubmit: () => void;
	isLoading?: boolean;
	designDirections: IDesignDirection[];
	setDesignDirections: React.Dispatch<
		React.SetStateAction<IDesignDirection[] | IDesignDirection[]>
	>;
	creatives: ICreative[];
	setCreatives: React.Dispatch<React.SetStateAction<ICreative[] | ICreative[]>>;
	availableChannelGroups: IChannelGroup[];
	selectedChannels: string[];
	isGeneratingFromModalShared?: boolean;
	setIsGeneratingDesignDirections?: React.Dispatch<
		React.SetStateAction<boolean>
	>;
}

const DesignDirections = ({
	creativesConfig,
	isLoading,
	designDirections,
	setDesignDirections,
	creatives,
	setCreatives,
	availableChannelGroups,
	selectedChannels,
	isGeneratingFromModalShared,
	setIsGeneratingDesignDirections,
	...props
}: DesignDirectionsProps) => {
	const {
		id: campaignId,
		setCampaign,
		campaign,
		completeStep,
		wasCampaignChanged,
		setIsEditorMode,
	} = useContext(CampaignContext);
	const { closeChat } = useContext(AssistantChatContext);
	const [selectedDesignDirectionId, setSelectedDesignDirectionId] = useState<
		string | null
	>(null);

	const { selectedTemplateIds, creatomateTemplates, layeredTemplates } =
		useContext(TemplateContext);

	const [fonts, setFonts] = useState<any>([]);
	const [selectedDesignDirection, setSelectedDesignDirection] =
		useState<IDesignDirection>();
	const [channels, setChannels] = useState<string[]>(campaign?.channels ?? []);
	const [loadingCreatives, setLoadingCreatives] = useState(false);
	const [isGeneratingDD, setIsGeneratingDD] = useState(false);
	const [isAutoAssign, setIsAutoAssign] = useState(false);
	const [isGeneratingFromModal, setIsGeneratingFromModal] = useState(false);
	const {
		isOpen: isConfirmDialogOpen,
		onOpen: onOpenConfirmDialog,
		onClose: onCloseConfirmDialog,
	} = useDisclosure();

	const [editingDesignDirection, setEditingDesingDirection] =
		useState<IDesignDirection>();
	const prevDesignDirectionsLength = useRef(designDirections.length);
	const tagModalToggle = useToggleWithPayload<IDesignDirection>();
	const skeletonsRef = useRef<HTMLDivElement | null>(null);
	const ddRefs = useRef<Array<HTMLElement | null>>([]);

	const timeoutId = useRef<any>(null);
	const pollingStartTime = useRef<number | null>(null);
	const lastValidCampaignId = useRef<string | null>(null);
	const creativesRef = useRef<any>(null);
	const hasAnyGenerated = designDirections?.some(
		(c) => c.status === 'GENERATED',
	);

	// Helper to get effective campaign ID from URL (context may have stale 'new' value)
	const getEffectiveCampaignId = () => {
		const urlMatch = window.location.pathname.match(/\/projects\/campaigns\/([^?/]+)/);
		const urlCampaignId = urlMatch ? urlMatch[1] : undefined;
		return (urlCampaignId && urlCampaignId !== 'new') 
			? urlCampaignId 
			: campaign?.id || campaignId;
	};

	// Helper to get campaign object with correct ID for API calls
	const getCampaignForApi = () => {
		const effectiveId = getEffectiveCampaignId();
		if (!effectiveId || effectiveId === 'new') return null;
		return campaign ? { ...campaign, id: effectiveId } : { id: effectiveId };
	};
	const [selectedTemplatesCount, setSelectedTemplatesCount] = useState<number>(
		selectedTemplateIds.length,
	);
	const isDesignDirectionPending = designDirections?.some(
		(c) => c.status === 'pending' || c.status === 'generating',
	);

	const isShowingSkeletons =
		(isLoading ||
			isGeneratingDD ||
			isGeneratingFromModal ||
			isGeneratingFromModalShared) &&
		designDirections.length === 0;

	// Debug: Log when the button should be disabled
	useEffect(() => {
		console.log('🔘 [DD] Button state check:', {
			isShowingSkeletons,
			isLoading,
			isGeneratingDD,
			isGeneratingFromModal,
			isGeneratingFromModalShared,
			designDirectionsCount: designDirections.length,
			campaignId,
			hasCampaign: !!campaign,
		});
	}, [
		isShowingSkeletons,
		isLoading,
		isGeneratingDD,
		isGeneratingFromModal,
		isGeneratingFromModalShared,
		designDirections.length,
	]);

	const getFontsAccount = async () => {
		const fontArray = await getFonts();
		setFonts(fontArray);
	};
	useEffect(() => {
		getFontsAccount();
	}, []);
	useEffect(() => {
		if (isGeneratingDD) {
			if (isAutoAssign) {
				setSelectedTemplatesCount(1);
			} else {
				setSelectedTemplatesCount(selectedTemplateIds.length);
			}
		}
	}, [isGeneratingDD, selectedTemplateIds.length, isAutoAssign]);

	useEffect(() => {
		if (!isGeneratingDD) {
			setIsAutoAssign(false);
		}
	}, [isGeneratingDD]);

	useEffect(() => {
		if (isGeneratingDD) {
			setSelectedTemplatesCount(isAutoAssign ? 1 : selectedTemplateIds.length);
		}
	}, [isGeneratingDD, selectedTemplateIds.length, isAutoAssign]);
	useEffect(() => {
		setSelectedTemplatesCount(selectedTemplateIds.length);
	}, [selectedTemplateIds]);

	useEffect(() => {
		if (
			!selectedDesignDirectionId &&
			designDirections?.length &&
			designDirections[0].status === 'GENERATED'
		) {
			handleSelectDesignDirection(designDirections[0], false);
		}
	}, [designDirections]);

	useEffect(() => {
		if (
			!timeoutId.current && // CRITICAL: Only start polling if no timeout is already running
			(isLoading || isDesignDirectionPending || isGeneratingFromModalShared)
		) {
			// Set polling start time when we begin polling for generation
			if (isGeneratingFromModalShared && !pollingStartTime.current) {
				pollingStartTime.current = Date.now();
			}
			handleRefetchCampaignDesignDirection();
		}
	}, [
		isDesignDirectionPending,
		timeoutId.current,
		isLoading,
		isGeneratingFromModalShared,
		campaign?.id,
	]);

	useEffect(() => {
		// Only update design directions if they actually changed and are not empty
		if (campaign?.designDirections && campaign.designDirections.length > 0) {
			setDesignDirections(removeDuplicateLayers(campaign.designDirections));
		}
		campaign?.creatives && setCreatives(campaign?.creatives);
		campaign?.channels && setChannels(campaign?.channels);
	}, [campaign?.designDirections, campaign?.creatives, campaign?.channels]);

	useEffect(() => {
		if (isLoading || !designDirections.length) {
			setSelectedDesignDirectionId(null);
		} else if (
			!selectedDesignDirectionId ||
			(selectedDesignDirection?.id === designDirections[0].id &&
				designDirections[0].status === 'GENERATED' &&
				selectedDesignDirection?.status !== 'GENERATED') ||
			designDirections.every((d) => d.status === 'pending')
		) {
			handleSelectDesignDirection(designDirections[0]);
		}
	}, [isLoading, designDirections]);

	useEffect(() => {
		if (designDirections.length > prevDesignDirectionsLength.current) {
			// Reset local loading states when new design directions arrive
			if (isGeneratingFromModal) {
				setIsGeneratingFromModal(false);
			}
			setIsGeneratingDD(false);

			// Also reset the shared state by calling CreateEditCampaign's setter
			if (isGeneratingFromModalShared && setIsGeneratingDesignDirections) {
				setIsGeneratingDesignDirections(false);
			}
		}
		prevDesignDirectionsLength.current = designDirections.length;
	}, [
		designDirections,
		isGeneratingFromModal,
		isGeneratingFromModalShared,
		setIsGeneratingDesignDirections,
	]);

	useEffect(() => {
		if (isGeneratingDD && skeletonsRef.current) {
			skeletonsRef.current.scrollIntoView({
				behavior: 'smooth',
				block: 'center',
			});
		}
	}, [isGeneratingDD]);

	useEffect(() => {
		if (designDirections.length > prevDesignDirectionsLength.current) {
			const lastDDIndex = designDirections.length - 1;
			const lastDDRef = ddRefs.current[lastDDIndex];
			if (lastDDRef) {
				lastDDRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
		prevDesignDirectionsLength.current = designDirections.length;
	}, [designDirections.length]);

	useEffect(() => {
		if (designDirections.length > prevDesignDirectionsLength.current) {
			const lastDDIndex = designDirections.length - 1;
			const lastDDRef = ddRefs.current[lastDDIndex];
			if (lastDDRef) {
				lastDDRef.scrollIntoView({ behavior: 'smooth', block: 'center' });
			}
		}
		prevDesignDirectionsLength.current = designDirections.length;
	}, [designDirections.length]);

	useEffect(() => {
		prevDesignDirectionsLength.current = designDirections.length;
	}, [designDirections]);

	useEffect(() => {
		if (editingDesignDirection) {
			setIsEditorMode(true); // Enable editor mode
			window.history.pushState({ editing: true }, '');

			const onPopState = () => {
				onOpenConfirmDialog();
			};

			window.addEventListener('popstate', onPopState);

			return () => {
				window.removeEventListener('popstate', onPopState);
			};
		} else {
			setIsEditorMode(false); // Disable editor mode when closing
		}
	}, [editingDesignDirection, setIsEditorMode]);

	const handleRefetchCampaignDesignDirection = async () => {
		try {
			// Use actual campaign ID if available, fallback to campaignId
			let effectiveCampaignId =
				campaignId === 'new' && campaign?.id ? campaign.id : campaignId;

			// Remember the last valid campaign ID to prevent reverting to 'new'
			if (effectiveCampaignId && effectiveCampaignId !== 'new') {
				lastValidCampaignId.current = effectiveCampaignId;
			} else if (lastValidCampaignId.current) {
				effectiveCampaignId = lastValidCampaignId.current;
			}

			if (!effectiveCampaignId || effectiveCampaignId === 'new') {
				return;
			}
			const response = await getCampaign(effectiveCampaignId);
			setDesignDirections(response.designDirections ?? []);
			const isPending = response.designDirections?.some(
				(c) => c.status === 'pending' || c.status === 'generating',
			);

			// For new campaigns where we just initiated generation, continue polling
			// even if no pending design directions are found yet (they may still be creating)
			// But add a timeout to prevent infinite polling
			const timeSincePollingStarted = pollingStartTime.current
				? Date.now() - pollingStartTime.current
				: 0;
			const maxPollingTime = 90000; // 90 seconds max

			const shouldContinuePolling =
				isPending ||
				(isGeneratingFromModalShared &&
					response.designDirections?.length === 0 &&
					timeSincePollingStarted < maxPollingTime);

			if (shouldContinuePolling) {
				timeoutId.current = setTimeout(
					handleRefetchCampaignDesignDirection,
					1000,
				);
			} else {
				setIsGeneratingDD(false);
				if (isGeneratingFromModal) {
					setIsGeneratingFromModal(false);
				}
				// Reset the shared state
				if (isGeneratingFromModalShared && setIsGeneratingDesignDirections) {
					setIsGeneratingDesignDirections(false);
				}
				// Reset polling start time
				pollingStartTime.current = null;
				clearTimeout(timeoutId.current);
				timeoutId.current = undefined;
			}
		} catch (error: any) {
			console.error('❌ Polling error:', error);
			toastError(error);
		}
	};

	const triggerPollingForNewGeneration = () => {
		// Force start polling when we know generation has started
		if (!timeoutId.current) {
			handleRefetchCampaignDesignDirection();
		}
	};

	const handleRefreshDesignDirectionsWithCount = async (count: number) => {
		setSelectedTemplatesCount(count);
		await handleRefreshDesignDirections();
	};

	const handleRetryGenerateDD = async (designDirection: IDesignDirection) => {
		const effectiveId = getEffectiveCampaignId();
		if (!effectiveId || effectiveId === 'new') return;
		try {
			setIsGeneratingDD(true);
			await regenerateCampaignDesignDirection(
				effectiveId,
				designDirection.id,
				{},
			);
			toastSuccess('Design direction regenerated');
			await handleRefreshDesignDirections();
		} catch (error) {
			toastError(error);
		} finally {
			setIsGeneratingDD(false);
		}
	};

	const handleNavigateToStep2 = () => {
		// Navigate to step 2 immediately
		completeStep(0);
	};

	const handleRefreshDesignDirections = async () => {
		const effectiveId = getEffectiveCampaignId();
		if (!effectiveId || effectiveId === 'new') return;
		const response = await getCampaign(effectiveId);
		if (response) {
			setDesignDirections(response.designDirections ?? []);
			setCampaign(response);
		}
	};

	const getNextTemplate = async () => {
		if (!campaignId) return;

		if (layeredTemplates?.length > 0 && !wasCampaignChanged) {
			return getHighestScoreTemplate(layeredTemplates);
		}

		const designDirectionTemplateIds =
			getTemplateIdsFromDesignDirections(designDirections);

		const filteredTemplates = creatomateTemplates.filter(
			(template) => !designDirectionTemplateIds.includes(template.templateId),
		);

		const highestScoreTemplate = getHighestScoreTemplate(filteredTemplates);

		return await processTemplate(highestScoreTemplate.id, campaign);
	};

	const processTemplate = async (
		templateId: string,
		campaign: ICampaign | null,
	) => {
		const { callback } = await getTemplateLayers(templateId, campaign);
		return await processCallback(callback);
	};

	const handleGenerateNewDD = async () => {
		const effectiveCampaignId = getEffectiveCampaignId();
		const campaignForApi = getCampaignForApi();

		console.log('🎨 [DD] Generate Design Started - Function called!', {
			currentSelectedChannels: selectedChannels,
			campaignChannels: campaign?.channels,
			campaignId,
			effectiveCampaignId,
			hasCampaign: !!campaign,
			campaignObjectId: campaign?.id,
		});

		try {
			setIsAutoAssign(true);

			if (!campaignForApi || !effectiveCampaignId || effectiveCampaignId === 'new') {
				console.error('🔴 [DD] No valid campaignId - aborting');
				return;
			}

			setIsGeneratingDD(true);

			console.log('🟢 [DD] Calling genrateNewDesignDirection API with ID:', effectiveCampaignId);
			await genrateNewDesignDirection(campaignForApi as any, { adEngine: 'creatomate' });

			console.log('🟢 [DD] API call completed, refreshing design directions...');
			await handleRefreshDesignDirectionsWithCount(1);

			console.log('✅ [DD] Generate Design Completed');
		} catch (error: any) {
			console.error('🔴 [DD] Error generating design direction:', error);
			toastError(error);
			setIsGeneratingDD(false);
		}
	};

	const handleRemoveDesignDirection = async (id: string) => {
		try {
			const campaignForApi = getCampaignForApi();
			if (!campaignForApi || !id) return;
			await removeDesignDirection(campaignForApi as any, id);
			toastSuccess('Design direction deleted');
			if (selectedDesignDirectionId === id) {
				setSelectedDesignDirectionId(null);
			}
			handleRefreshDesignDirections();
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleGenerateCreatives = async (
		designDirectionId?: string,
		onlyUnprocessed = true,
	) => {
		const campaignForApi = getCampaignForApi();
		const effectiveId = getEffectiveCampaignId();
		if (!campaignForApi || !effectiveId) return;
		try {
			console.log('🎯 Generate Creatives Started:', {
				designDirectionId,
				currentSelectedChannels: selectedChannels,
				campaignChannels: campaign?.channels,
				onlyUnprocessed,
				effectiveId,
			});
			
			await genrateCampaignCreatives(
				campaignForApi as any,
				designDirectionId,
				onlyUnprocessed,
			);
			const data = await getCampaign(effectiveId);
			
			console.log('🎯 Generate Creatives - Fresh data from backend:', {
				newChannels: data.channels,
				currentSelectedChannels: selectedChannels,
				needsChannelSync: JSON.stringify(selectedChannels) !== JSON.stringify(data.channels)
			});
			
			setChannels(data.channels);
			data.creatives && setCreatives(data.creatives);
		} catch (error: any) {
			toastError(error);
		}
	};

	const handleSelectDesignDirection = (
		designDirection: IDesignDirection,
		scroll = true,
	) => {
		if (selectedDesignDirectionId !== designDirection.id) {
			setSelectedDesignDirectionId(designDirection.id);
			setSelectedDesignDirection(designDirection);
			scroll && creativesRef.current.scrollIntoView();
		}
		if (designDirection.status !== 'GENERATED') return;

		const creativesFromSelectedDD = creatives?.filter(
			(creative) => creative.designDirectionId === designDirection.id,
		);
		const allChannelsCovered = selectedChannels.every((channel) =>
			creativesFromSelectedDD?.some((creative) => creative.channel === channel),
		);
		const hasPendingCreatives =
			!creativesFromSelectedDD.length || !allChannelsCovered;

		if (hasPendingCreatives && designDirection.status === 'GENERATED') {
			setLoadingCreatives(true);
			handleGenerateCreatives(designDirection.id, hasPendingCreatives);
		}
	};

	const handleCloseEditor = () => {
		setEditingDesingDirection(undefined);
	};

	const handleSave = async (updatedTemplate: any) => {
		const campaignForApi = getCampaignForApi();
		const effectiveId = getEffectiveCampaignId();
		if (!campaignForApi || !editingDesignDirection) return;
		const updatedPayload = { attributes: updatedTemplate };

		await editCampaignDesignDirections(
			campaignForApi as any,
			editingDesignDirection.id,
			updatedPayload,
		);

		await handleRefreshDesignDirections();
		handleCloseEditor();
		toastSuccess('Your changes have been saved');

		try {
			setLoadingCreatives(true);
			await genrateCampaignCreatives(campaignForApi as any, editingDesignDirection.id);
			const data = await getCampaign(effectiveId!);
			setCampaign(data);
			toastSuccess('Creatives have been regenerated successfully');
		} catch (error) {
			toastError('Failed to regenerate creatives');
		} finally {
			setLoadingCreatives(false);
		}
	};

	const handleChange = (updatedTemplate: any) => {
		setEditingDesingDirection((prev) => {
			if (!prev) return prev;
			return {
				...prev,
				attributes: { ...prev.attributes, elements: updatedTemplate.elements },
			};
		});
	};

	const isAnySelected = selectedDesignDirectionId !== null;
	const sortedDesignDirections = [...designDirections].sort((a, b) => {
		if (a.locked && !b.locked) return -1;
		if (!a.locked && b.locked) return 1;
		return a.variant.localeCompare(b.variant);
	});
	const designDirectionsToShow = isLoading
		? sortedDesignDirections.filter((dd) => dd.locked)
		: sortedDesignDirections;

	const isEditing = Boolean(editingDesignDirection);

	if (isEditing)
		return (
			<>
				<ConfirmDialog
					isOpen={isConfirmDialogOpen}
					onClose={onCloseConfirmDialog}
					onConfirm={() => {
						handleCloseEditor();
						onCloseConfirmDialog();
					}}
					message="You have unsaved changed that will be lost if you decide to continue. Are you sure you want to leave this page?"
				/>
				<div
					style={{
						left: 0,
						top: 72,
						width: `calc(100vw - ${RIGHT_SIDEBAR_WIDTH_COLLAPSED})`,
						height: '100vh',
						zIndex: 1000,
						position: 'fixed',
					}}
				>
					<Design
						fonts={fonts}
						template={editingDesignDirection!.attributes}
						onClose={handleCloseEditor}
						onSave={handleSave}
						onChange={handleChange}
					/>
				</div>
			</>
		);

	return (
		<Flex p={{ base: 2, md: 4 }} direction="column" w="full" overflowX="hidden">
			<HStack
				justifyContent="space-between"
				w="full"
				alignItems="center"
				mb={{ base: 6, md: 10 }}
				mt={{ base: '-10px', md: '-20px' }}
				flexWrap="wrap"
				gap={2}
			>
				<Text
					size="md"
					visibility={
						designDirections.length > 0 || isShowingSkeletons
							? 'visible'
							: 'hidden'
					}
				>
					Designs to promote your business
				</Text>

				<GenerateOrSelectCard
					onGenerate={handleGenerateNewDD}
					campaignId={campaignId!}
					setIsGeneratingDD={setIsGeneratingDD}
					setIsGeneratingFromModal={setIsGeneratingFromModal}
					isDisabled={isShowingSkeletons}
					source="designDirections"
					handleRefreshDesignDirections={handleRefreshDesignDirections}
					triggerPollingForNewGeneration={triggerPollingForNewGeneration}
					handleNavigateToStep2={handleNavigateToStep2}
				/>
			</HStack>
			<Wrap spacing={{ base: 2, md: 4 }} justify="center" w="full">
				{designDirections.length > 0 &&
					designDirections.map((dd: IDesignDirection, index) => (
						<WrapItem
							key={dd.id}
							ref={(el: HTMLLIElement | null) => (ddRefs.current[index] = el)}
							maxW="100%"
						>
							<Box maxW="100%">
								<DDCard
									designDirection={dd}
									openEditModal={() => setEditingDesingDirection(dd)}
									onRetry={handleRetryGenerateDD}
									onRemove={handleRemoveDesignDirection}
									onSelect={() => handleSelectDesignDirection(dd)}
									isSelected={selectedDesignDirectionId === dd.id}
									isAnySelected={isAnySelected}
								/>
							</Box>
						</WrapItem>
					))}

				{(isLoading || isGeneratingDD || isGeneratingFromModal) &&
					Array.from({ length: selectedTemplatesCount || 1 }).map(
						(_, index) => (
							<WrapItem key={`skeleton-${index}`}>
								<Box ref={index === 0 ? skeletonsRef : undefined}>
									<Skeleton w="323px" h="323px" borderRadius="16px" />
								</Box>
							</WrapItem>
						),
					)}
				{(() => {
					const shouldShowEmptyState =
						designDirections.length === 0 &&
						!isLoading &&
						!isGeneratingDD &&
						!isGeneratingFromModal &&
						!isGeneratingFromModalShared;

					return shouldShowEmptyState ? <EmptyState /> : null;
				})()}
			</Wrap>

			<Box ref={creativesRef} />
			{designDirections.length > 0 &&
				selectedDesignDirection &&
				selectedChannels.length > 0 && (
					<Creatives
						creatives={creatives}
						setCreatives={setCreatives}
						loadingCreatives={loadingCreatives}
						setLoadingCreatives={setLoadingCreatives}
						channels={selectedChannels}
						designDirection={selectedDesignDirection}
						creativesConfig={creativesConfig}
						availableChannelGroups={availableChannelGroups}
					/>
				)}
			{designDirections.length > 0 && (
				<Box alignSelf="flex-end" mt={5}>
					<Button
						isDisabled={!hasAnyGenerated || isDesignDirectionPending}
						variant="orangeSolid"
						_hover={{ opacity: 0.9 }}
						_disabled={{ opacity: 0.7, cursor: 'not-allowed' }}
						onClick={() => {
							props.onSubmit();
							completeStep(1);
						}}
					>
						Schedule & Publish
					</Button>
				</Box>
			)}
			{/* {tagModalToggle.payload && (
				<EditDDModal
					isOpen={tagModalToggle.isOpen}
					onClose={() => {
						tagModalToggle.onClose();
						closeChat();
					}}
					initialValues={tagModalToggle.payload}
					handleRefreshDesignDirections={handleRefreshDesignDirections}
					designDirections={designDirections}
					setLoadingCreatives={setLoadingCreatives}
				/>
			)} */}
		</Flex>
	);
};
export default DesignDirections;
